import React, { useContext, useEffect } from 'react';
import { TextLink } from '@spotify-internal/encore-web';
import { Tags } from '@spotify-internal/semantic-metrics';

import { PageId } from 'utils/ubi/pageId';
import analytics from 'utils/client/analytics/analytics';
import { MarketContext } from 'providers/market';
import { setNavigationInteraction } from 'utils/ubi/navigation';
import { UbiNavigationFactory } from 'utils/ubi/types';
import { useUbiContext } from 'utils/ubi/ubiProvider';
import { sendCounterMetric, Whats } from 'utils/metrics';
import { useVerticalConfig } from 'providers/vertical';

const dictToQueryString = (params?: Record<string, string>) => {
  const query = Object.entries(params ?? {})
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  if (query !== '') {
    return `?${query}`;
  }
  return '';
};

// LinkProps should expose same functionality as Encore TextLink, except for exposing the href.
// Feel free to refactor if you really need to use onClick.
export type LinkProps = (Parameters<typeof TextLink>[0] & { onClick?: never; href?: never }) & {
  queryParams?: Record<string, string>;
  eventDefinition?: Parameters<typeof analytics.trackEvent>[0];
  ubiFactory?: UbiNavigationFactory;
  sendMetrics?: { what: Whats; tags?: Tags };
};

export type HrefFunction = (market: string, verticalParam: string) => string;

export type InternalLinkProps = LinkProps & {
  toPage: PageId;
  hrefFun: HrefFunction;
};

const InternalLink = ({
  toPage,
  hrefFun,
  eventDefinition,
  queryParams,
  ubiFactory,
  sendMetrics,
  ...linkProps
}: InternalLinkProps) => {
  const { marketCode } = useContext(MarketContext);
  const { pathParam, verticalName } = useVerticalConfig();
  const href = `${hrefFun(marketCode, pathParam)}${dictToQueryString(queryParams)}`;
  const ubiLogger = useUbiContext();

  useEffect(() => {
    if (ubiFactory) {
      ubiLogger.logImpression(ubiFactory.impression());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Always emit only one impression per entry

  const onClick = () => {
    if (eventDefinition) {
      analytics.trackEvent(eventDefinition);
    }
    if (ubiFactory) {
      const interactionId = ubiLogger.logInteraction(ubiFactory.hitUiNavigate({ destination: href }));
      setNavigationInteraction(href, interactionId);
    }

    if (sendMetrics) {
      sendCounterMetric(sendMetrics.what, { ...sendMetrics.tags, vertical: verticalName });
    }
  };
  return <TextLink {...linkProps} href={href} onClick={onClick} />;
};

export default InternalLink;
